import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ExternalLink, X } from "lucide-react";

export default function BookingOverlay() {
  const [src, setSrc] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!src) {
      return;
    }
    const html = document.documentElement;
    const prevHtml = html.style.overflow;
    const prevBody = document.body.style.overflow;
    html.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    if (window.__lenis) window.__lenis.stop();
    return () => {
      html.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
      if (window.__lenis) window.__lenis.start();
    };
  }, [src]);

  useEffect(() => {
    const onClick = (e) => {
      const link = e.target && e.target.closest ? e.target.closest('a[href*="palisis.com"]') : null;
      if (!link) return;
      // links flagged data-bypass-overlay (e.g. the navbar Buy Tickets) go straight to a new tab
      if (link.hasAttribute("data-bypass-overlay")) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.defaultPrevented) return;
      e.preventDefault();
      setLoaded(false);
      setSrc(link.href);
    };
    const onOpen = (e) => {
      const product = e.detail && e.detail.product ? e.detail.product : null;
      setLoaded(false);
      setSrc(product ? `https://coolrunnings-capetown.palisis.com/?book-direct=${encodeURIComponent(product)}` : "https://coolrunnings-capetown.palisis.com/");
    };
    const onKey = (e) => {
      if (e.key === "Escape") setSrc(null);
    };
    window.addEventListener("click", onClick, true);
    window.addEventListener("cr:open-booking", onOpen);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", onClick, true);
      window.removeEventListener("cr:open-booking", onOpen);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <AnimatePresence>
      {src && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.18, ease: "easeIn" } }}
          className="fixed inset-0 z-[95] flex items-end justify-end bg-[#0a0a0b]/80 p-4 backdrop-blur-sm sm:p-6"
          onClick={() => setSrc(null)}
          data-testid="booking-overlay"
        >
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0, transition: { duration: 0.22, ease: "easeIn" } }}
            transition={{ type: "spring", stiffness: 240, damping: 26 }}
            className="relative flex h-[80vh] w-full max-w-[600px] flex-col overflow-hidden rounded-[24px] bg-[#fff7e8] shadow-[0_0_0_3px_#f8b818,0_0_0_9px_rgba(248,184,24,0.16),0_40px_90px_rgba(20,18,12,0.6)]"
            onClick={(e) => e.stopPropagation()}
            data-testid="booking-overlay-card"
          >
            <div className="flex h-14 shrink-0 items-center justify-between gap-3 bg-gradient-to-b from-[#f9c92e] to-[#f8b818] px-4 sm:px-5">
              <h3 className="text-xl leading-none text-[#0a0a0b] sm:text-2xl" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>
                Buy your tickets
              </h3>
              <div className="flex items-center gap-2">
                <a
                  href={src}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-bypass-overlay
                  onClick={(e) => {
                    // guarantee a real new tab on every device, even if another
                    // global handler interferes with the anchor's default action
                    e.preventDefault();
                    e.stopPropagation();
                    window.open(src, "_blank", "noopener,noreferrer");
                  }}
                  className="flex items-center gap-1.5 rounded-full border border-[#0a0a0b]/25 px-3 py-2 text-[10px] font-extrabold uppercase tracking-widest text-[#0a0a0b] transition-colors hover:bg-[#0a0a0b]/10 sm:py-1.5"
                  data-testid="booking-overlay-fallback"
                >
                  <ExternalLink size={12} /> <span className="sm:hidden">New tab</span>
                  <span className="hidden sm:inline">Open in new tab</span>
                </a>
                <button
                  onClick={() => setSrc(null)}
                  className="rounded-full border border-[#0a0a0b]/25 bg-white/60 p-1.5 text-[#0a0a0b] transition-colors hover:bg-[#0a0a0b]/10"
                  aria-label="Close booking"
                  data-testid="booking-overlay-close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            {!loaded && (
              <div className="absolute inset-0 top-14 z-10 flex flex-col items-center justify-center gap-3 bg-[#fff7e8]" data-testid="booking-overlay-loading">
                <span className="h-9 w-9 animate-spin rounded-full border-[3px] border-[#f8b818] border-t-transparent" />
                <p className="text-xs font-extrabold uppercase tracking-widest text-[#6b5a3a]">Loading the ticket shop…</p>
              </div>
            )}
            {/* if the embedded shop can't display (some networks block embedding), use Open in new tab in the header */}
            <iframe
              title="Cool Runnings ticket shop"
              src={src}
              onLoad={() => setLoaded(true)}
              className="h-full w-full flex-1 border-0"
              allow="payment"
              referrerPolicy="strict-origin-when-cross-origin"
              data-testid="booking-overlay-iframe"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}